import type { ColdStorageRestoreOps, ColdStorageRestoreResponse } from '@mimir/client-common/coldStorage/coldStorageRestoreOps';
import { ColdStorageRestoreImpl, type PostRestoreRequestResponse } from '@mimir/client-common/coldStorage/coldStorageRestoreImpl';
import { postRestoreMedia } from '@mimir/web/util/api';
import { RestoreTier } from '@mimir/common/dto/restoreMediaRequestDto';
import { instanceOfRestoreMediaResponsePayload, type RestoreMediaModel, type RestoreMediaResponseDto } from '@mimir/common/dto/restoreMediaDto';
import { isObject } from '@mimir/primitive/type-guards/isObject';
import type { RestoredItem } from '@mimir/client-common/coldStorage/coldStoragePollOps';
import { getColdStoragePollers } from './coldStoragePolling';
import { ArchiveRestoreStatus } from '@mimir/common/dto/itemPropTypes';
import { TOAST, showToast } from '@mimir/web/eventBus';
import type { LocalStateOps } from '@mimir/client-common/fileSync/localStateOps';
import type { UploadContext, UploadItem } from '@mimir/client-common/upload/UploadItem';
import type { AdobePanelContext, ElectronContext } from '@mimir/common/context/applicationContext';
import type { Logger } from '@mimir/common/types/logger';
import { premiere } from './premiere';
import { mediaRestored } from './mediaRestored';
import type { DownloadAndRelinkOps } from '@mimir/client-common/fileSync/downloadAndRelinkOps';
import type { NotificationOps } from '@mimir/client-common/fileSync/notificationOps';
import type { OngoingDownloadOps } from '@mimir/client-common/fileSync/ongoingDownloadOps';
import type { ProjectOps } from '@mimir/client-common/fileSync/projectOps';
import type { UploadOps } from '@mimir/client-common/fileSync/uploadOps';
import type { BasicUploadManager } from '@mimir/common/upload/BasicUploadManager';
import { getGlobalStore } from '@mimir/web/store/index';


/**
 * This interface describes all the parameters needed to construct
 * a DefaultColdStorageRestoreOps object.
 *
 * Even though some properties are not needed for the restore process itself,
 * they are required to import the item after restoration has taken place.
 * Once the download code is made fully awaitable, we will be able to take out the
 * import trigger out of DefaultColdStorageRestoreOps and replace it with something
 * like:
 *
 *   await restore(...);
 *   await import(...);
 *
 * at which point all these properties would be removed from the restore code.
 */
export interface PremiereRestoreParameters {
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
    uploadContext: UploadContext,
    uploadManager: BasicUploadManager,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps,
    projectOps: ProjectOps,
    uploadOps: UploadOps,
    downloadAndRelinkOps: DownloadAndRelinkOps,
  },
  config: {
    downloadPath: string,
    isStandaloneMode: boolean,
    isProxyMode: boolean,
    userDataSystemPath: string,
    shouldUsePremierePreferences: boolean,
  },
  ongoingUploads: UploadItem[],
}


/**
 * This class is capable of initiating a restore from cold storage
 * that will take care of monitoring the restore and updating the UI accordingly.
 * Moreover, if we are in Premiere, the premiereRestoreParameters parameter should
 * be set with all the required information so that the item can be synced if needed
 * once the highres has been restored.
 */
export class DefaultColdStorageRestoreOps implements ColdStorageRestoreOps {

  private delegate: ColdStorageRestoreOps;

  constructor(
    premiereRestoreParameters: PremiereRestoreParameters | undefined,
  ) {
    const { expeditedPollOps, nonExpeditedPollOps } = getColdStoragePollers();

    const processRestoredItem = async (item: RestoredItem) => processRestoredItemInternal(
      item,
      premiereRestoreParameters,
    );

    this.delegate = new ColdStorageRestoreImpl(
      postRestoreMediaRequest,
      processRestoreResponse,
      processRestoredItem,
      expeditedPollOps,
      nonExpeditedPollOps,
    );
  }

  requestRestore(itemId: string, restoreTier: RestoreTier, retentionDays: number): Promise<ColdStorageRestoreResponse> {
    return this.delegate.requestRestore(itemId, restoreTier, retentionDays);
  }

}



async function processRestoredItemInternal(
  item: RestoredItem,
  premiereRestoreParameters: PremiereRestoreParameters | undefined,
): Promise<void> {
  const restoreCompletedPayload = {
    itemId: item.id,
    highRes: item.highRes,
    mediaOnlineUntil: item.archiveInfo?.mediaOnlineUntil,
  };
  const store = getGlobalStore();
  if (store.getters.currentItem && store.getters.currentItem.id === item.id) {
    store.commit('setRestoreMediaCompletedInCurrentItem', restoreCompletedPayload);
  }
  store.commit('setRestoreMediaCompletedInSearchResult', restoreCompletedPayload);

  if (premiereRestoreParameters) {
    await mediaRestored(premiereRestoreParameters.services, premiereRestoreParameters.config, premiere.clipInfos, premiereRestoreParameters.ongoingUploads, restoreCompletedPayload.itemId);
  }
}


async function processRestoreResponse(itemId: string, requestedTier: RestoreTier, response: PostRestoreRequestResponse): Promise<void> {
  const store = getGlobalStore();
  if (response.objectOnline) {
    const payload: RestoreMediaResponseDto = {
      itemId,
      highRes: response.highresUrl,
    };
    await store.dispatch('restoreMediaCompletedForOnlineMedia', payload);
  }

  const currentItem = store.getters.currentItem;
  if (currentItem && currentItem.id === itemId && !response.objectOnline) {
    store.commit('setMediaRestoreStatusOfCurrentItem', ArchiveRestoreStatus.InProgress);
  }

  store.commit('setMediaRestoreStatusInSearchResult', {
    itemId,
    restoreStatus: ArchiveRestoreStatus.InProgress,
  });

  if (response.objectOnline || response.restoreStatus.restoreWasAlreadyInProgress) {
    return;
  }

  const expeditedServiceNotAvailable = requestedTier === RestoreTier.Expedited && response.restoreStatus.effectiveRestoreTier !== RestoreTier.Expedited;
  if (expeditedServiceNotAvailable) {
    showToast('The AWS express restore service is busy at this moment, using Standard mode to restore the media.', TOAST.WARNING);
  } else {
    showToast('Restore request sent successfully', TOAST.SUCCESS);
  }
}


async function postRestoreMediaRequest(itemId: string, tier: RestoreTier, retentionDays: number): Promise<PostRestoreRequestResponse> {
  const payload: RestoreMediaModel = {
    itemId,
    tier,
    retentionDays,
  };
  try {
    const response = await postRestoreMedia(payload);
    if (isObject(response) && response.status === 201 && response.data) {
      const item: unknown = response.data;
      if (instanceOfRestoreMediaResponsePayload(item)) {
        return {
          objectOnline: true,
          highresUrl: item.highRes,
        };
      }
    }

    const expeditedServiceNotAvailable = response.status === 202;
    const effectiveRestoreTier = expeditedServiceNotAvailable ? RestoreTier.Standard : tier;
    return {
      objectOnline: false,
      restoreStatus: {
        restoreWasAlreadyInProgress: false,
        effectiveRestoreTier,
      }
    };
  } catch (err) {
    if ((err as any)?.response?.status === 409) {
      return {
        objectOnline: false,
        restoreStatus: {
          restoreWasAlreadyInProgress: true,
        },
      };
    }

    throw err;
  }

}
